import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client";
import type { AdminAnnouncement, Announcement, ServiceState } from "../api/types";

/** How each service status reads to people, and the colour it uses. */
export const SERVICE_STATES: Record<
  ServiceState,
  { label: string; tone: "ok" | "warning" | "danger" | "info"; fallback: string }
> = {
  operational: { label: "Working normally", tone: "ok", fallback: "" },
  degraded: {
    label: "Slow or partly working",
    tone: "warning",
    fallback: "Some people may have problems. We're looking into it.",
  },
  outage: { label: "Not working", tone: "danger", fallback: "We know it's down and we're working on it." },
  maintenance: {
    label: "Planned maintenance",
    tone: "info",
    fallback: "It may be unavailable while the work is done.",
  },
};

const ANNOUNCEMENTS = ["announcements"] as const;
const ADMIN_ANNOUNCEMENTS = ["admin", "announcements"] as const;

/** What's live now, newest first. */
export function useAnnouncements() {
  return useQuery({
    queryKey: ANNOUNCEMENTS,
    queryFn: ({ signal }) => api<Announcement[]>("/api/announcements", { signal }),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export function useAdminAnnouncements() {
  return useQuery({
    queryKey: ADMIN_ANNOUNCEMENTS,
    queryFn: ({ signal }) => api<AdminAnnouncement[]>("/api/admin/announcements", { signal }),
  });
}

export interface AnnouncementInput {
  title: string;
  body: string;
  starts_at: string | null;
  ends_at: string | null;
}

function useRefreshAnnouncements() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ADMIN_ANNOUNCEMENTS }),
      queryClient.invalidateQueries({ queryKey: ANNOUNCEMENTS }),
    ]);
}

export function useSaveAnnouncement(id: string | undefined) {
  const refresh = useRefreshAnnouncements();
  return useMutation({
    mutationFn: (body: AnnouncementInput) =>
      id
        ? api<AdminAnnouncement>(`/api/admin/announcements/${encodeURIComponent(id)}`, { method: "PUT", body })
        : api<AdminAnnouncement>("/api/admin/announcements", { method: "POST", body }),
    onSuccess: refresh,
  });
}

export function useDeleteAnnouncement(id: string) {
  const refresh = useRefreshAnnouncements();
  return useMutation({
    mutationFn: () => api<undefined>(`/api/admin/announcements/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: refresh,
  });
}

export function useSetServiceStatus(serviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { status: ServiceState; message: string | null }) =>
      api(`/api/admin/services/${encodeURIComponent(serviceId)}/status`, { method: "PUT", body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["catalogue"] }),
  });
}
